import type { ThreatModelContent } from '../../utils/api-client/api-schemas.ts'
import {
  bucketizeThreat,
  buildCoverageModel,
  coerceRelevance,
  type ThreatRelevance,
} from '../../utils/threat-model/threat-coverage.ts'

/**
 * Turns a threat model into the left-to-right path it describes: who can reach the system, through
 * what, which defenses stand in the way, which weaknesses are present, and what is at risk.
 *
 * **Every edge is stated by the payload — and the view adapts to how much the payload states.** The
 * generator's linking pass (analysis-service#2927) emits four relationships as typed id references:
 * `actors[].reaches` (actor→entry point), `entry_points[].exposes` (entry point→weakness),
 * `vulnerability_class_impacts[].affects` (weakness→asset), and the older
 * `security_controls[].mitigates` (control→weakness). When a document carries them, all five columns
 * are drawn and every curve is a claim the model made.
 *
 * **Older documents carry only `mitigates`.** Every threat model currently in storage predates the
 * linking pass, and the fields are optional, so a document may arrive with none of the reference
 * edges. Rather than draw three columns with nothing connecting them — or invent edges from prose,
 * which is what the retired markdown view did and got wrong — the view collapses to the two columns
 * the older payload does connect: security controls and the weaknesses they mitigate. The full graph
 * lights up on its own once the generator starts emitting the edges; no code change gates it.
 *
 * The reference fields point at each entity's minted `id`; our node ids are name-derived (actors,
 * entry points, assets) or taxonomy-merged (vulnerabilities), so each id is translated to a node id
 * before an edge can land — see {@link buildAttackPaths}. Columns 3 and 4 and the control→weakness
 * edge come from {@link buildCoverageModel} rather than repeating its taxonomy join here, so
 * "vulnerability" means the same thing here as it did in Threat coverage, whose gap language this view
 * absorbed.
 */

export const ATTACK_PATH_COLUMNS = [
  { key: 'actor', title: 'Actors' },
  { key: 'entry', title: 'Entry points' },
  { key: 'control', title: 'Security controls' },
  { key: 'vuln', title: 'Vulnerabilities' },
  { key: 'asset', title: 'Sensitive assets' },
] as const

export type AttackPathColumnKey = (typeof ATTACK_PATH_COLUMNS)[number]['key']

export type AttackPathField = { key: string; value: string }

export type AttackPathNode = {
  id: string
  column: AttackPathColumnKey
  label: string
  /** The one categorical fact worth carrying on the node: trust level, kind, or relevance. */
  tag?: string
  /** Relevance, on vulnerability nodes only — drives the colour ramp inherited from Threat coverage. */
  relevance?: ThreatRelevance
  /** A present or likely weakness that no control claims to mitigate. */
  isGap?: boolean
  /**
   * Ids of the controls whose own `mitigates` list names this weakness — the one relationship the
   * payload states, and so the only place this view can honestly say a path is contested.
   */
  defendedBy?: string[]
  /** The reader's route back to the code, where the payload gives one. */
  location?: string
  /** Payload prose for the detail pane, in the order the reader wants it. */
  fields: AttackPathField[]
}

export type AttackPathLink = {
  from: string
  to: string
}

export type AttackPathsModel = {
  nodes: AttackPathNode[]
  links: AttackPathLink[]
}

/** Ids are name-derived rather than positional, so a fixture keyed to them survives a reordering. */
export const attackPathNodeId = (column: AttackPathColumnKey, name: string): string =>
  `${column}:${name
    .trim()
    .toLowerCase()
    .replace(/[^a-z0-9]+/g, '-')
    .replace(/^-|-$/g, '')}`

const withoutEmpty = (fields: (AttackPathField | undefined)[]): AttackPathField[] =>
  fields.filter((field): field is AttackPathField => !!field && field.value.trim().length > 0)

const field = (key: string, value: string | undefined): AttackPathField | undefined =>
  value === undefined ? undefined : { key, value }

export type BuildAttackPathsInput = {
  content: ThreatModelContent
}

/** Indexes entities that carry a minted `id` by that id, mapping each to its name-derived node id. */
const nodeIdByBackendId = <T extends { id?: string }>(
  items: readonly T[],
  toNodeId: (item: T) => string
): Map<string, string> => {
  const map = new Map<string, string>()
  for (const item of items) {
    if (item.id !== undefined) {
      map.set(item.id, toNodeId(item))
    }
  }
  return map
}

export const buildAttackPaths = ({ content }: BuildAttackPathsInput): AttackPathsModel => {
  const coverage = buildCoverageModel({
    internetFacing: content.exposure.internet_facing,
    controls: content.security_controls.map(control => ({
      name: control.name,
      kind: control.kind,
      mitigates: control.mitigates,
      scope: control.scope,
    })),
    vulnerabilities: content.vulnerability_class_impacts.map(impact => ({
      vulnClass: impact.vuln_class,
      relevance: coerceRelevance(impact.relevance),
      reachability: impact.reachability,
      blastRadius: impact.blast_radius,
    })),
  })

  const actorNodes: AttackPathNode[] = content.actors.map(actor => ({
    id: attackPathNodeId('actor', actor.name),
    column: 'actor',
    label: actor.name,
    tag: actor.trust_level,
    fields: withoutEmpty([field('Can reach', actor.can_reach), field('Authenticates via', actor.authenticates_via)]),
  }))

  const entryNodes: AttackPathNode[] = content.entry_points.map(entryPoint => ({
    id: attackPathNodeId('entry', entryPoint.name),
    column: 'entry',
    label: entryPoint.name,
    tag: entryPoint.kind,
    location: entryPoint.location,
    fields: withoutEmpty([field('Description', entryPoint.description), field('Auth', entryPoint.authentication)]),
  }))

  const controlNodes: AttackPathNode[] = coverage.controls.map(control => ({
    id: `control:${control.id}`,
    column: 'control',
    label: control.name,
    tag: control.kind,
    fields: withoutEmpty([field('Scope', control.scope), field('Mitigates', control.mitigates.join(', '))]),
  }))

  const vulnNodes: AttackPathNode[] = coverage.threats.map(threat => ({
    id: `vuln:${threat.id}`,
    column: 'vuln',
    label: threat.name,
    tag: threat.relevance,
    relevance: threat.relevance,
    isGap: threat.isGap,
    defendedBy: threat.mitigatingControlIds.map(controlId => `control:${controlId}`),
    fields: withoutEmpty([field('Reachability', threat.reachability), field('Blast radius', threat.blastRadius)]),
  }))

  const assetNodes: AttackPathNode[] = content.sensitive_assets.map(asset => ({
    id: attackPathNodeId('asset', asset.name),
    column: 'asset',
    label: asset.name,
    tag: asset.kind,
    location: asset.location,
    fields: withoutEmpty([field('Description', asset.description)]),
  }))

  const entryNodeByBackendId = nodeIdByBackendId(content.entry_points, entry => attackPathNodeId('entry', entry.name))
  const assetNodeByBackendId = nodeIdByBackendId(content.sensitive_assets, asset =>
    attackPathNodeId('asset', asset.name)
  )

  // One weakness id can resolve to several vulnerability nodes: `buildCoverageModel` merges impacts
  // that share a canonical threat name and keeps an unrecognised class verbatim, so the same
  // taxonomy join has to be replayed here to reach the node(s) a backend id became.
  const vulnNodeIdByName = new Map(coverage.threats.map(threat => [threat.name, `vuln:${threat.id}`]))
  const resolveImpactNodes = (vulnClass: string): string[] => {
    const names = bucketizeThreat(vulnClass)
    const threatNames = names.length > 0 ? names : [vulnClass]
    return threatNames.flatMap(name => {
      const nodeId = vulnNodeIdByName.get(name)
      return nodeId ? [nodeId] : []
    })
  }
  const vulnNodesByBackendId = new Map<string, string[]>()
  for (const impact of content.vulnerability_class_impacts) {
    if (impact.id !== undefined) {
      vulnNodesByBackendId.set(impact.id, resolveImpactNodes(impact.vuln_class))
    }
  }

  // actor→entry point. The source id is always the actor's own name-derived node; only the targets
  // need translating, and a reference to an id this document does not carry is dropped.
  const reachesLinks: AttackPathLink[] = content.actors.flatMap(actor =>
    (actor.reaches ?? []).flatMap(entryId => {
      const to = entryNodeByBackendId.get(entryId)
      return to ? [{ from: attackPathNodeId('actor', actor.name), to }] : []
    })
  )

  // entry point→weakness.
  const exposesLinks: AttackPathLink[] = content.entry_points.flatMap(entry =>
    (entry.exposes ?? []).flatMap(vulnId =>
      (vulnNodesByBackendId.get(vulnId) ?? []).map(to => ({ from: attackPathNodeId('entry', entry.name), to }))
    )
  )

  // weakness→asset. The source is the impact's own resolved node(s); the targets are asset ids.
  const affectsLinks: AttackPathLink[] = content.vulnerability_class_impacts.flatMap(impact => {
    if (impact.id === undefined) {
      return []
    }
    const fromNodes = vulnNodesByBackendId.get(impact.id) ?? []
    return (impact.affects ?? []).flatMap(assetId => {
      const to = assetNodeByBackendId.get(assetId)
      return to ? fromNodes.map(from => ({ from, to })) : []
    })
  })

  // control→weakness, the one relationship even a pre-linking payload states, via `mitigates`.
  const mitigatesLinks: AttackPathLink[] = coverage.controls.flatMap(control =>
    control.coveredThreatIds.map(threatId => ({ from: `control:${control.id}`, to: `vuln:${threatId}` }))
  )

  // A column earns its place only when it holds nodes and connects to the drawn graph. Controls and
  // weaknesses are the core the older payload always states; the outer three appear once the linking
  // pass supplies the edges that reach them. So a pre-linking document renders as two columns, and a
  // linked one as up to five, through the same layout — no separate "legacy" view.
  const activeColumns = new Set<AttackPathColumnKey>()
  if (controlNodes.length > 0) {
    activeColumns.add('control')
  }
  if (vulnNodes.length > 0) {
    activeColumns.add('vuln')
  }
  if (actorNodes.length > 0 && reachesLinks.length > 0) {
    activeColumns.add('actor')
  }
  if (entryNodes.length > 0 && (reachesLinks.length > 0 || exposesLinks.length > 0)) {
    activeColumns.add('entry')
  }
  if (assetNodes.length > 0 && affectsLinks.length > 0) {
    activeColumns.add('asset')
  }

  const nodes = [...actorNodes, ...entryNodes, ...controlNodes, ...vulnNodes, ...assetNodes].filter(node =>
    activeColumns.has(node.column)
  )
  const nodeIds = new Set(nodes.map(node => node.id))

  // An edge to a node no active column holds is dropped rather than drawn to nowhere — the same
  // guard the layout applies, kept here so the model is coherent on its own.
  const links = [...reachesLinks, ...exposesLinks, ...affectsLinks, ...mitigatesLinks].filter(
    link => nodeIds.has(link.from) && nodeIds.has(link.to)
  )

  return { nodes, links }
}
